import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'

// Other components open the modal by dispatching this event on `window`.
export const OPEN_SEARCH_EVENT = 'help:open-search'

// Shape of `/search-index.json`, emitted by scripts/generate-ai-artifacts.ts.
// Kept in sync with `SearchPage` in scripts/lib/ai-artifacts.ts — the build
// runs the unit tests first, so a shape drift fails CI.
interface IndexPage {
  slug: string
  title: string
  description: string
  section: string
  category: string
  keywords: string[]
  headings: string[]
  body: string
}
interface SearchIndex {
  version: number
  pages: IndexPage[]
}

interface Hit {
  slug: string
  title: string
  excerpt: string
}

// Score weights per field. Higher = more important.
const W_TITLE = 100
const W_HEADING = 25
const W_KEYWORD = 20
const W_DESCRIPTION = 10
const W_BODY = 4
// Partial-match (one token missing) penalty: divide the score by this much.
const PARTIAL_DIVISOR = 6

// One module-level promise so the index is fetched at most once per page load,
// even if the modal is opened and closed repeatedly.
let indexPromise: Promise<SearchIndex | null> | null = null

function loadIndex(): Promise<SearchIndex | null> {
  if (indexPromise) return indexPromise
  indexPromise = fetch('/search-index.json', { credentials: 'omit' })
    .then((r) => (r.ok ? (r.json() as Promise<SearchIndex>) : null))
    .catch(() => null)
  return indexPromise
}

function tokenize(query: string): string[] {
  return query
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 0)
}

function countOccurrences(haystack: string, needle: string): number {
  if (!needle) return 0
  let count = 0
  let idx = 0
  while ((idx = haystack.indexOf(needle, idx)) !== -1) {
    count += 1
    idx += needle.length
  }
  return count
}

function scorePage(page: IndexPage, tokens: string[]): number {
  const title = page.title.toLowerCase()
  const headings = page.headings.join(' \n ').toLowerCase()
  const keywords = page.keywords.join(' \n ').toLowerCase()
  const description = page.description.toLowerCase()
  const body = page.body.toLowerCase()
  let score = 0
  let matchedAll = true
  for (const t of tokens) {
    let tokenHit = false
    const inTitle = countOccurrences(title, t)
    if (inTitle > 0) {
      score += W_TITLE * inTitle
      tokenHit = true
    }
    const inHeadings = countOccurrences(headings, t)
    if (inHeadings > 0) {
      score += W_HEADING * inHeadings
      tokenHit = true
    }
    const inKeywords = countOccurrences(keywords, t)
    if (inKeywords > 0) {
      score += W_KEYWORD * inKeywords
      tokenHit = true
    }
    const inDescription = countOccurrences(description, t)
    if (inDescription > 0) {
      score += W_DESCRIPTION * inDescription
      tokenHit = true
    }
    const inBody = countOccurrences(body, t)
    if (inBody > 0) {
      // Cap body contribution so an essay full of "the" doesn't crowd out a
      // hub page whose title is exactly the query.
      score += W_BODY * Math.min(inBody, 5)
      tokenHit = true
    }
    if (!tokenHit) matchedAll = false
  }
  // Exact-phrase bonus: a page whose title or description literally contains
  // the user's typed query (joined) should beat one that just matches the
  // bag of tokens.
  const phrase = tokens.join(' ')
  if (phrase.length > 0) {
    if (title.includes(phrase)) score += W_TITLE
    if (description.includes(phrase)) score += W_DESCRIPTION * 2
  }
  return matchedAll ? score : Math.floor(score / PARTIAL_DIVISOR)
}

function buildExcerpt(page: IndexPage, tokens: string[]): string {
  const text = page.body || page.description
  if (!text) return ''
  const lower = text.toLowerCase()
  let firstIdx = -1
  for (const t of tokens) {
    const i = lower.indexOf(t)
    if (i >= 0 && (firstIdx === -1 || i < firstIdx)) firstIdx = i
  }
  const RADIUS_BEFORE = 50
  const RADIUS_AFTER = 130
  if (firstIdx < 0) {
    const end = Math.min(text.length, RADIUS_AFTER + RADIUS_BEFORE)
    return text.slice(0, end) + (text.length > end ? '…' : '')
  }
  // Snap the window to word boundaries so we don't slice mid-word.
  let start = Math.max(0, firstIdx - RADIUS_BEFORE)
  let end = Math.min(text.length, firstIdx + RADIUS_AFTER)
  if (start > 0) {
    const space = text.indexOf(' ', start)
    if (space !== -1 && space < firstIdx) start = space + 1
  }
  if (end < text.length) {
    const space = text.lastIndexOf(' ', end)
    if (space > firstIdx) end = space
  }
  return (
    (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '')
  )
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function highlight(text: string, tokens: string[]): ReactNode {
  if (tokens.length === 0) return text
  const pattern = new RegExp(`(${tokens.map(escapeRegex).join('|')})`, 'gi')
  const parts = text.split(pattern)
  return parts.map((part, i) =>
    i % 2 === 1 ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>,
  )
}

function search(index: SearchIndex, query: string, limit = 10): Hit[] {
  const tokens = tokenize(query)
  if (tokens.length === 0) return []
  const scored: Array<{ p: IndexPage; score: number }> = []
  for (const p of index.pages) {
    const score = scorePage(p, tokens)
    if (score > 0) scored.push({ p, score })
  }
  scored.sort((a, b) => b.score - a.score || a.p.title.localeCompare(b.p.title))
  return scored.slice(0, limit).map(({ p }) => ({
    slug: p.slug,
    title: p.title,
    excerpt: buildExcerpt(p, tokens),
  }))
}

export default function SearchModal() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [index, setIndex] = useState<SearchIndex | null>(null)
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'error'>(
    'idle',
  )
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  // Open with Cmd/Ctrl-K or OPEN_SEARCH_EVENT; close with Escape.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((v) => !v)
      } else if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    function onOpen(): void {
      setOpen(true)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener(OPEN_SEARCH_EVENT, onOpen)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener(OPEN_SEARCH_EVENT, onOpen)
    }
  }, [])

  // Load the index the first time the modal opens. Subsequent opens reuse it.
  useEffect(() => {
    if (!open || index || loadState === 'loading') return
    setLoadState('loading')
    void loadIndex().then((i) => {
      if (i) {
        setIndex(i)
        setLoadState('idle')
      } else {
        setLoadState('error')
      }
    })
  }, [open, index, loadState])

  // Focus the input on open and lock background scroll. Reset on close.
  useEffect(() => {
    if (!open) {
      setQuery('')
      setActive(0)
      return
    }
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const focusId = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => {
      document.body.style.overflow = prevOverflow
      window.clearTimeout(focusId)
    }
  }, [open])

  const tokens = useMemo(() => tokenize(query), [query])
  const hits = useMemo(
    () => (index ? search(index, query) : []),
    [index, query],
  )

  // Keep `active` in range when the hit list shrinks.
  useEffect(() => {
    setActive((a) => (hits.length === 0 ? 0 : Math.min(a, hits.length - 1)))
  }, [hits.length])

  const go = useCallback(
    (slug: string) => {
      setOpen(false)
      navigate(slug)
    },
    [navigate],
  )

  if (!open) return null

  const hasQuery = query.trim().length > 0

  return (
    <div
      className="search-overlay"
      onClick={() => setOpen(false)}
      role="presentation"
    >
      <div
        className="search-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Search the docs"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="search-input-row">
          <svg
            className="search-input-icon"
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden
          >
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
            <path
              d="M13.5 13.5L17 17"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          <input
            ref={inputRef}
            className="search-input"
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="Search the docs…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault()
                setActive((a) => Math.min(a + 1, Math.max(0, hits.length - 1)))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setActive((a) => Math.max(a - 1, 0))
              } else if (e.key === 'Enter' && hits[active]) {
                e.preventDefault()
                go(hits[active].slug)
              }
            }}
            aria-controls="search-results"
            aria-activedescendant={
              hits[active] ? `search-hit-${active}` : undefined
            }
          />
          <button
            type="button"
            className="search-close"
            aria-label="Close search"
            onClick={() => setOpen(false)}
          >
            Esc
          </button>
        </div>
        <ul id="search-results" className="search-results" role="listbox">
          {loadState === 'loading' && !index && (
            <li className="search-status">Loading search…</li>
          )}
          {loadState === 'error' && (
            <li className="search-status">
              Couldn't load the search index. Refresh the page and try again.
            </li>
          )}
          {hits.map((hit, i) => (
            <li key={hit.slug} role="presentation">
              <button
                id={`search-hit-${i}`}
                type="button"
                role="option"
                aria-selected={i === active}
                className={i === active ? 'search-hit is-active' : 'search-hit'}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(hit.slug)}
              >
                <span className="search-hit-title">
                  {highlight(hit.title, tokens)}
                </span>
                {hit.excerpt && (
                  <span className="search-hit-excerpt">
                    {highlight(hit.excerpt, tokens)}
                  </span>
                )}
              </button>
            </li>
          ))}
          {index && hasQuery && hits.length === 0 && loadState === 'idle' && (
            <li className="search-status">No results for "{query.trim()}".</li>
          )}
          {!hasQuery && index && (
            <li className="search-hint">
              Type to search — try "scheduling", "resume parsing", or "API key".
            </li>
          )}
        </ul>
      </div>
    </div>
  )
}
